// AppThreadsApiProvider — mounts ThreadsApiProvider with a module-level singleton
// client so thread hooks (sidebar, chat session) work without per-route wiring.

import { type ReactNode } from 'react';
import { createApiClient } from '@/shared/api';
import { getToken } from '@/shared/auth';
import { readEnv } from '@/shared/config';
import { ThreadsApiProvider } from '@/entities/thread';
import type { ThreadsApiClient } from '@/entities/thread';

const authProvider = { getToken };

let _threadsClient: ThreadsApiClient | null = null;

function sessionPath(threadId: string, suffix = ''): string {
  return `/api/v1/sessions/${encodeURIComponent(threadId)}${suffix}`;
}

function getThreadsClient(): ThreadsApiClient {
  if (!_threadsClient) {
    const baseUrl = readEnv('VITE_API_BASE_URL') ?? '';
    const client = createApiClient({ baseUrl, auth: authProvider });
    _threadsClient = {
      create: (agentId, signal) =>
        client.request({
          method: 'POST',
          path: '/api/v1/sessions',
          body: { agent_id: agentId },
          signal,
        }),
      fetchList: (request, signal) =>
        client.request({
          path: '/api/v1/sessions',
          query: {
            limit: request.limit,
            offset: request.offset,
            cursor: request.cursor,
            agent_id: request.agentId,
            prompted_only: request.promptedOnly,
          },
          signal,
        }),
      fetchPromptedAgentIds: (signal) =>
        client.request({
          path: '/api/v1/sessions/prompted-agents',
          signal,
        }),
      fetchDetail: (threadId, signal) =>
        client.request({
          path: sessionPath(threadId),
          signal,
        }),
    };
  }
  return _threadsClient;
}

export interface AppThreadsApiProviderProps {
  children: ReactNode;
  /** Override the API client — intended for tests only. */
  client?: ThreadsApiClient;
}

export function AppThreadsApiProvider({ children, client }: AppThreadsApiProviderProps) {
  return (
    <ThreadsApiProvider client={client ?? getThreadsClient()}>
      {children}
    </ThreadsApiProvider>
  );
}
